// ─── Billing calculation helpers ────────────────────────────────────────────
//
// Pure functions for computing discounts and line totals from bill/quotation
// line items. Extracted from BillConflictView so they can be independently
// unit-tested and reused across conflict views.
//
// All amounts are in paise (BIGINT). GST rounding uses Math.floor() per
// CLAUDE.md conventions.

export interface BillLineItem {
  quantity?: number;
  unit_price?: number;
  discount_type?: 'PERCENTAGE' | 'FLAT';
  discount_value?: number;
}

/**
 * Compute the discount amount in paise for a bill line item.
 * PERCENTAGE: Math.floor(qty * unitPrice * value / 100)
 * FLAT: direct value (capped at 0 minimum)
 */
export function computeDiscountPaise(item: BillLineItem): number {
  const qty = Number(item.quantity ?? 0);
  const unitPrice = Number(item.unit_price ?? 0);
  const value = Number(item.discount_value ?? 0);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice) || !Number.isFinite(value)) {
    return 0;
  }
  if (item.discount_type === 'PERCENTAGE') {
    return Math.floor((qty * unitPrice * value) / 100);
  }
  if (item.discount_type === 'FLAT') {
    return Math.max(0, value);
  }
  return 0;
}

/**
 * Compute the line total in paise: qty * unitPrice - discount.
 * Always returns >= 0.
 */
export function computeLineTotal(item: BillLineItem): number {
  const qty = Number(item.quantity ?? 0);
  const unitPrice = Number(item.unit_price ?? 0);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return Math.max(0, qty * unitPrice - computeDiscountPaise(item));
}

/**
 * Compute the line total for a quotation line item (flat discount only).
 */
export function computeQuotationLineTotal(item: { quantity?: number; unit_price?: number; discount?: number }): number {
  const qty = Number(item.quantity ?? 0);
  const unitPrice = Number(item.unit_price ?? 0);
  const discount = Number(item.discount ?? 0);
  if (!Number.isFinite(qty) || !Number.isFinite(unitPrice)) return 0;
  return Math.max(0, qty * unitPrice - (Number.isFinite(discount) ? discount : 0));
}
